import { Crown, LogOut, Pencil, Stoke, Threads, UserMinus, UserPlus } from '@/components/ui/icons';
import { useEffect, type ReactNode } from 'react';
import { extractMentionIds, type MessageDTO } from '@clanfire/shared';
import { userApi } from '@/api/endpoints';
import { formatLong, formatMessageTime } from '@/lib/time';
import { cn } from '@/lib/utils';
import { useUi } from '@/stores/ui';
import { useUser, useUsers } from '@/stores/users';
import { ThreadChip } from './ThreadChip';

const requested = new Set<string>();

/** Loads someone the client hasn't seen yet (e.g. a person who has since left the group). */
function useEnsureUser(id: string | undefined) {
  const known = useUsers((s) => (id ? !!s.users[id] : true));
  useEffect(() => {
    if (!id || known || requested.has(id)) return;
    requested.add(id);
    userApi
      .profile(id)
      .then((p) => useUsers.getState().upsert([p.user]))
      .catch(() => undefined);
  }, [id, known]);
}

/** A note about a change ("Alex added Sam to the group"), centred in the history. */
export function SystemMessage({ message, highlighted }: { message: MessageDTO; highlighted: boolean }) {
  const actor = useUser(message.author?.id, message.author);
  const subjectId = extractMentionIds(message.content)[0];
  const subject = useUser(subjectId);
  useEnsureUser(message.author?.id);
  useEnsureUser(subjectId);
  const openModal = useUi((s) => s.openModal);

  const name = (user: typeof actor, id: string | undefined) =>
    id ? (
      <button type="button" onClick={() => openModal({ type: 'profile', userId: id })} className="font-semibold text-fg hover:underline">
        {user?.displayName ?? 'Deleted User'}
      </button>
    ) : (
      <strong className="font-semibold text-fg">Deleted User</strong>
    );
  const who = name(actor, message.author?.id);
  const whom = name(subject, subjectId);

  let icon: ReactNode;
  let text: ReactNode;
  switch (message.type) {
    case 'GROUP_ADD':
      icon = <UserPlus size={16} className="text-success" />;
      text = <>{who} added {whom} to the group.</>;
      break;
    case 'GROUP_REMOVE':
      icon = <UserMinus size={16} className="text-danger" />;
      text = <>{who} removed {whom} from the group.</>;
      break;
    case 'GROUP_LEAVE':
      icon = <LogOut size={16} className="text-danger" />;
      text = <>{who} left the group.</>;
      break;
    case 'GROUP_RENAME':
      icon = <Pencil size={16} />;
      text = message.content ? (
        <>
          {who} renamed the group to <strong className="font-semibold text-fg">{message.content}</strong>.
        </>
      ) : (
        <>{who} removed the group's name.</>
      );
      break;
    case 'GROUP_OWNER':
      icon = <Crown size={16} className="text-warning" />;
      text = <>{who} made {whom} the group owner.</>;
      break;
    case 'THREAD_CREATED':
      icon = <Threads size={16} className="text-accent" />;
      text = (
        <>
          {who} started a thread: <strong className="font-semibold text-fg">{message.thread?.name ?? message.content}</strong>.
        </>
      );
      break;
    case 'STOKE':
      icon = <Stoke size={16} className="text-[#f97316]" />;
      text = message.content ? (
        <>
          {who} stoked the fire! This server is now <strong className="font-semibold text-fg">{message.content}</strong>.
        </>
      ) : (
        <>{who} stoked the fire!</>
      );
      break;
    default:
      return null;
  }

  return (
    <div
      id={`message-${message.id}`}
      role="article"
      data-cf="system-message"
      className={cn('mt-3 flex items-start gap-3 px-4 py-1 text-sm text-fg-muted sm:px-6', highlighted && 'animate-flash')}
    >
      <span className="flex w-[42px] shrink-0 justify-center pt-0.5" aria-hidden>
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <p>
          {text}{' '}
          <time dateTime={message.createdAt} title={formatLong(message.createdAt)} className="text-xs">
            {formatMessageTime(message.createdAt)}
          </time>
        </p>
        {message.thread && <ThreadChip thread={message.thread} />}
      </div>
    </div>
  );
}
